import { useEffect, useMemo, useRef } from 'react'
import {
  buildMdDocumentModel,
  getActiveMatchWithinSection,
  getSectionById,
  type MdDocumentSection,
} from './mdDocumentModel'
import { clearActiveSearchHighlights, highlightActiveSearchMatch } from './mdTextActiveMatchHighlight'
import { useMdTextSearchIndex } from './useMdTextSearchIndex'

export function useMdTextSearch({
  searchValue,
  isEditing,
  content,
  selectedSectionId,
  onSelectSection,
  highlightContainerRef,
  highlightSectionBody,
  sections: providedSections,
}: {
  searchValue: string
  isEditing: boolean
  content: string
  sections?: MdDocumentSection[]
  selectedSectionId?: string
  onSelectSection?: (sectionId: string) => void
  highlightContainerRef?: React.RefObject<HTMLDivElement | null>
  highlightSectionBody?: string
}) {
  const sections = useMemo(
    () => providedSections ?? buildMdDocumentModel(content).sections,
    [content, providedSections]
  )

  const contentRef = useRef<HTMLDivElement>(null)
  const {
    matches,
    activeMatch,
    hasSearchTerm,
    isSearchPending,
    matchCount,
    activeMatchIndex,
    goToPreviousMatch,
    goToNextMatch,
  } = useMdTextSearchIndex({
    searchValue,
    isEditing,
    content,
    sections,
  })

  useEffect(() => {
    if (!hasSearchTerm || !activeMatch || !onSelectSection) {
      return
    }
    if (selectedSectionId === activeMatch.sectionId) {
      return
    }
    onSelectSection(activeMatch.sectionId)
  }, [activeMatch, hasSearchTerm, onSelectSection, selectedSectionId])

  const resolvedHighlightRef = highlightContainerRef ?? contentRef

  useEffect(() => {
    const container = resolvedHighlightRef.current
    const sectionBody = highlightSectionBody
    if (!container || !sectionBody || isEditing || !hasSearchTerm || !activeMatch) {
      if (container) {
        clearActiveSearchHighlights(container)
      }
      return
    }

    const section = getSectionById(sections, activeMatch.sectionId)
    if (!section) {
      return
    }

    const range = getActiveMatchWithinSection(section, activeMatch)
    requestAnimationFrame(() => {
      highlightActiveSearchMatch(container, sectionBody, range)
    })
  }, [activeMatch, hasSearchTerm, sections, highlightSectionBody, isEditing, resolvedHighlightRef])

  return {
    contentRef,
    matches,
    activeMatch,
    hasSearchTerm,
    isSearchPending,
    matchCount,
    activeMatchIndex,
    goToPreviousMatch,
    goToNextMatch,
  }
}

export function getSectionForSearchNavigation(
  sections: MdDocumentSection[],
  sectionId: string | undefined
): MdDocumentSection | undefined {
  if (!sectionId) {
    return sections[0]
  }
  return getSectionById(sections, sectionId) ?? sections[0]
}
